import React, { useState, useEffect } from 'react';
import {
  Plus, Edit2, Trash2, X, Save, Trophy, ChevronRight,
  LayoutList, Swords, Loader2, Image as ImageIcon,
} from 'lucide-react';
import { useToast } from '../../../context/ToastContext';
import {
  fetchAllBanners, createBanner, updateBanner, deleteBanner,
  Banner, BannerInput,
} from '../../../services/bannerService';
import { ConfirmModal } from '../../Common/ConfirmModal';

type WcClickAction = 'league' | 'scenario' | 'knockout';
type WcKnockoutRound = 'r32' | 'r16' | 'qf' | 'sf' | 'final';

const POSITION_OPTIONS = ['main', 'sub', 'popup'];
const POSITION_LABELS: Record<string, string> = { main: '메인', sub: '서브', popup: '팝업' };

const ROUND_OPTIONS: { id: WcKnockoutRound; label: string }[] = [
  { id: 'r32', label: '32강' },
  { id: 'r16', label: '16강' },
  { id: 'qf', label: '8강' },
  { id: 'sf', label: '4강' },
  { id: 'final', label: '결승' },
];

interface FormData {
  title_ko: string;
  title_en: string;
  desc_ko: string;
  desc_en: string;
  image_url: string;
  link_url: string;
  position: string;
  is_published: boolean;
  sort_order: number;
  start_at: string;
  end_at: string;
  banner_type: string;
  meta: { click_action: WcClickAction; knockout_round: WcKnockoutRound };
}

const defaultForm: FormData = {
  title_ko: '', title_en: '',
  desc_ko: '', desc_en: '',
  image_url: '', link_url: '',
  position: 'main',
  is_published: true,
  sort_order: 0,
  start_at: '', end_at: '',
  banner_type: 'generic',
  meta: { click_action: 'scenario', knockout_round: 'r32' },
};

const bannerToForm = (b: Banner): FormData => ({
  title_ko: b.title_ko ?? '',
  title_en: b.title_en ?? '',
  desc_ko: b.desc_ko ?? '',
  desc_en: b.desc_en ?? '',
  image_url: b.image_url ?? '',
  link_url: b.link_url ?? '',
  position: b.position,
  is_published: b.is_published,
  sort_order: b.sort_order,
  start_at: b.start_at ? b.start_at.slice(0, 16) : '',
  end_at: b.end_at ? b.end_at.slice(0, 16) : '',
  banner_type: b.banner_type ?? 'generic',
  meta: {
    click_action: (b.meta?.click_action as WcClickAction) ?? 'scenario',
    knockout_round: (b.meta?.knockout_round as WcKnockoutRound) ?? 'r32',
  },
});

const formToInput = (form: FormData): Omit<BannerInput, never> => ({
  title_ko: form.title_ko || null,
  title_en: form.title_en || null,
  desc_ko: form.desc_ko || null,
  desc_en: form.desc_en || null,
  image_url: form.image_url || null,
  link_url: form.banner_type !== 'worldcup' ? (form.link_url || null) : null,
  position: form.position,
  is_published: form.is_published,
  sort_order: form.sort_order,
  start_at: form.start_at ? new Date(form.start_at).toISOString() : null,
  end_at: form.end_at ? new Date(form.end_at).toISOString() : null,
  banner_type: form.banner_type,
  meta: form.banner_type === 'worldcup' ? form.meta : null,
});

export const BannerTab: React.FC = () => {
  const { showToast } = useToast();
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isFormAnimating, setIsFormAnimating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormData>(defaultForm);
  const [deleteTarget, setDeleteTarget] = useState<Banner | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setBanners(await fetchAllBanners());
    } catch {
      showToast('error', '배너 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openForm = () => {
    setIsFormOpen(true);
    setTimeout(() => setIsFormAnimating(true), 10);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(defaultForm);
    openForm();
  };

  const openEdit = (b: Banner) => {
    setEditingId(b.id);
    setForm(bannerToForm(b));
    openForm();
  };

  const closeForm = () => {
    if (saving) return;
    setIsFormAnimating(false);
    setTimeout(() => setIsFormOpen(false), 500);
  };

  const up = (patch: Partial<FormData>) => setForm(prev => ({ ...prev, ...patch }));
  const upMeta = (patch: Partial<FormData['meta']>) =>
    setForm(prev => ({ ...prev, meta: { ...prev.meta, ...patch } }));

  const handleSave = async () => {
    if (!form.title_ko && !form.title_en) {
      showToast('error', '제목을 입력해주세요.');
      return;
    }
    setSaving(true);
    try {
      const input = formToInput(form);
      if (editingId) {
        await updateBanner(editingId, input);
        showToast('success', '배너가 수정되었습니다.');
      } else {
        await createBanner(input);
        showToast('success', '배너가 등록되었습니다.');
      }
      closeForm();
      await load();
    } catch {
      showToast('error', '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteBanner(deleteTarget.id);
      showToast('success', '배너가 삭제되었습니다.');
      setDeleteTarget(null);
      await load();
    } catch {
      showToast('error', '삭제에 실패했습니다.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-8 pb-20">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tighter uppercase">배너 관리</h1>
          <p className="text-sm font-medium text-slate-500 mt-1">홈 화면 배너를 등록, 수정, 삭제합니다.</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-xl text-xs font-black uppercase tracking-widest shadow-lg active:scale-95 transition-all"
        >
          <Plus size={14} /> 배너 등록
        </button>
      </div>

      {/* Banner List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-slate-400" size={28} />
        </div>
      ) : banners.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 opacity-30 gap-4">
          <ImageIcon size={48} strokeWidth={1} />
          <p className="text-sm font-black uppercase tracking-widest">등록된 배너가 없습니다</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {banners.map(b => (
            <BannerCard key={b.id} banner={b} onEdit={() => openEdit(b)} onDelete={() => setDeleteTarget(b)} />
          ))}
        </div>
      )}

      {/* Form Modal */}
      {isFormOpen && (
        <div className={`fixed inset-0 z-[350] flex flex-col justify-end md:items-center md:justify-center transition-opacity duration-500 !mt-0 ${isFormAnimating ? 'opacity-100' : 'opacity-0'}`}>
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={closeForm} />
          <div className={`relative z-10 w-full max-w-2xl h-[90vh] flex flex-col bg-white rounded-t-[40px] md:rounded-[40px] shadow-2xl overflow-hidden transition-transform duration-700 ease-[0.32,0.72,0,1] ${isFormAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
            {/* Modal Header */}
            <div className="flex-shrink-0 bg-white px-8 pt-8 pb-4 flex items-center justify-between border-b border-slate-100">
              <h2 className="text-lg font-black text-slate-900 uppercase tracking-tight">
                {editingId ? '배너 수정' : '배너 등록'}
              </h2>
              <button onClick={closeForm} className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 active:scale-90 transition-all">
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
            <div className="p-8 space-y-6">
              {/* Banner Type */}
              <div className="space-y-2">
                <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest">배너 유형</label>
                <div className="flex gap-3">
                  {(['generic', 'worldcup'] as const).map(type => (
                    <button
                      key={type}
                      onClick={() => up({ banner_type: type })}
                      className={`flex-1 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider border-2 transition-all active:scale-95 ${
                        form.banner_type === type
                          ? 'bg-slate-900 text-white border-slate-900'
                          : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'
                      }`}
                    >
                      {type === 'worldcup' ? '⚽ 월드컵' : '일반'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Titles */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { key: 'title_ko', label: '제목 (한국어)', placeholder: '한국어 제목' },
                  { key: 'title_en', label: 'Title (English)', placeholder: 'English title' },
                  { key: 'desc_ko', label: '설명 (한국어)', placeholder: '한국어 설명' },
                  { key: 'desc_en', label: 'Description (English)', placeholder: 'English description' },
                ].map(({ key, label, placeholder }) => (
                  <div key={key} className="space-y-1.5">
                    <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest">{label}</label>
                    <input
                      value={(form as any)[key]}
                      onChange={e => up({ [key]: e.target.value } as any)}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm font-medium text-slate-900 focus:outline-none focus:border-slate-400 transition-colors"
                      placeholder={placeholder}
                    />
                  </div>
                ))}
              </div>

              {/* URLs */}
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest">이미지 URL</label>
                  <input
                    value={form.image_url}
                    onChange={e => up({ image_url: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm font-medium text-slate-900 focus:outline-none focus:border-slate-400"
                    placeholder="/assets/banner.png 또는 https://..."
                  />
                </div>
                {form.banner_type !== 'worldcup' && (
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest">링크 URL</label>
                    <input
                      value={form.link_url}
                      onChange={e => up({ link_url: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm font-medium text-slate-900 focus:outline-none focus:border-slate-400"
                      placeholder="https://..."
                    />
                  </div>
                )}
              </div>

              {/* Position / Sort / Published */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest">위치</label>
                  <select
                    value={form.position}
                    onChange={e => up({ position: e.target.value })}
                    className="w-full px-3 py-3 rounded-xl border border-slate-200 text-sm font-medium text-slate-900 bg-white focus:outline-none focus:border-slate-400"
                  >
                    {POSITION_OPTIONS.map(p => (
                      <option key={p} value={p}>{POSITION_LABELS[p] ?? p}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest">순서</label>
                  <input
                    type="number"
                    value={form.sort_order}
                    onChange={e => up({ sort_order: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm font-medium text-slate-900 focus:outline-none focus:border-slate-400"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest">노출</label>
                  <button
                    onClick={() => up({ is_published: !form.is_published })}
                    className={`w-full py-3 rounded-xl border-2 text-xs font-black uppercase tracking-wider transition-all ${
                      form.is_published
                        ? 'bg-emerald-50 border-emerald-400 text-emerald-700'
                        : 'bg-slate-50 border-slate-200 text-slate-400'
                    }`}
                  >
                    {form.is_published ? '노출 중' : '숨김'}
                  </button>
                </div>
              </div>

              {/* Schedule */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest">시작일시</label>
                  <input
                    type="datetime-local"
                    value={form.start_at}
                    onChange={e => up({ start_at: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm font-medium text-slate-900 focus:outline-none focus:border-slate-400"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest">종료일시</label>
                  <input
                    type="datetime-local"
                    value={form.end_at}
                    onChange={e => up({ end_at: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm font-medium text-slate-900 focus:outline-none focus:border-slate-400"
                  />
                </div>
              </div>

              {/* WC-specific settings */}
              {form.banner_type === 'worldcup' && (
                <div className="space-y-5 p-6 bg-slate-50 rounded-2xl border border-slate-100">
                  <h3 className="text-xs font-black text-slate-700 uppercase tracking-widest flex items-center gap-2">
                    <Trophy size={14} className="text-amber-500" /> 월드컵 배너 동작 설정
                  </h3>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">배너 클릭 동작</label>
                    <div className="grid grid-cols-3 gap-2">
                      {([
                        { id: 'league' as WcClickAction, label: '리그 단계', icon: LayoutList },
                        { id: 'scenario' as WcClickAction, label: '경우의 수', icon: Trophy },
                        { id: 'knockout' as WcClickAction, label: '녹아웃', icon: Swords },
                      ]).map(opt => (
                        <button
                          key={opt.id}
                          onClick={() => upMeta({ click_action: opt.id })}
                          className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 text-center transition-all active:scale-95 ${
                            form.meta.click_action === opt.id
                              ? 'border-slate-900 bg-slate-900 text-white'
                              : 'border-slate-200 bg-white text-slate-400'
                          }`}
                        >
                          <opt.icon size={16} />
                          <span className="text-[9px] font-black uppercase tracking-wider">{opt.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className={`space-y-2 transition-opacity ${form.meta.click_action === 'knockout' ? 'opacity-100' : 'opacity-30 pointer-events-none'}`}>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">기본 라운드</label>
                    <div className="flex flex-wrap gap-2">
                      {ROUND_OPTIONS.map(r => (
                        <button
                          key={r.id}
                          onClick={() => upMeta({ knockout_round: r.id })}
                          className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest border-2 transition-all active:scale-95 ${
                            form.meta.knockout_round === r.id
                              ? 'bg-rose-500 border-rose-500 text-white'
                              : 'bg-white border-slate-200 text-slate-500'
                          }`}
                        >
                          {r.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* WC Preview */}
                  <div className="relative overflow-hidden rounded-2xl bg-slate-900 p-4 flex items-center gap-4">
                    <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-800 to-rose-900 opacity-80" />
                    <div className="shrink-0 w-10 h-10 rounded-xl bg-white/10 border border-white/10 flex items-center justify-center relative z-10">
                      <img src={form.image_url || '/assets/fiwc.png'} alt="" className="w-7 h-7 object-contain" />
                    </div>
                    <div className="relative z-10 min-w-0">
                      <p className="text-[8px] font-black text-rose-400 uppercase tracking-[0.15em]">FIFA World Cup 2026</p>
                      <p className="text-sm font-black text-white leading-tight truncate">{form.title_ko || '배너 제목'}</p>
                      <p className="text-[10px] text-white/50 mt-0.5 truncate">{form.desc_ko || '배너 설명'}</p>
                    </div>
                    <div className="relative z-10 shrink-0 flex items-center gap-1 bg-white/10 border border-white/10 rounded-full px-3 py-1.5 ml-auto">
                      <span className="text-[9px] font-black text-white uppercase tracking-widest">보기</span>
                      <ChevronRight size={11} className="text-white" strokeWidth={3} />
                    </div>
                  </div>
                </div>
              )}

              {/* Save */}
              <button
                onClick={handleSave}
                disabled={saving}
                className="w-full flex items-center justify-center gap-2 py-4 bg-slate-900 text-white rounded-xl text-sm font-black uppercase tracking-widest shadow-lg active:scale-[0.98] transition-all disabled:opacity-50"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                {editingId ? '수정 저장' : '배너 등록'}
              </button>
            </div>
            </div>{/* flex-1 overflow-y-auto */}
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => !isDeleting && setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="배너를 삭제하시겠습니까?"
        confirmLabel="삭제"
        cancelLabel="취소"
        isLoading={isDeleting}
      />
    </div>
  );
};

// ─── Banner Card ────────────────────────────────────────────
const BannerCard: React.FC<{ banner: Banner; onEdit: () => void; onDelete: () => void }> = ({
  banner, onEdit, onDelete,
}) => {
  const isWC = banner.banner_type === 'worldcup';

  return (
    <div className={`rounded-[28px] border overflow-hidden shadow-sm ${isWC ? 'border-amber-200 bg-amber-50/20' : 'border-slate-200 bg-white'}`}>
      {/* Preview */}
      {isWC ? (
        <div className="relative overflow-hidden bg-slate-900 p-5 flex items-center gap-4">
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-800 to-rose-900 opacity-80" />
          <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-rose-600/20 to-transparent" />
          <div className="shrink-0 w-10 h-10 rounded-xl bg-white/10 border border-white/10 flex items-center justify-center relative z-10">
            <img src={banner.image_url ?? '/assets/fiwc.png'} alt="" className="w-7 h-7 object-contain" onError={e => { e.currentTarget.src = '/assets/fiwc.png'; }} />
          </div>
          <div className="relative z-10 min-w-0">
            <p className="text-[8px] font-black text-rose-400 uppercase tracking-[0.15em]">FIFA World Cup 2026</p>
            <p className="text-sm font-black text-white truncate">{banner.title_ko ?? banner.title_en ?? '월드컵 배너'}</p>
            {banner.desc_ko && <p className="text-[10px] text-white/40 truncate mt-0.5">{banner.desc_ko}</p>}
          </div>
        </div>
      ) : banner.image_url ? (
        <div className="h-28 bg-slate-100 overflow-hidden">
          <img src={banner.image_url} alt="" className="w-full h-full object-cover" onError={e => { e.currentTarget.style.display = 'none'; }} />
        </div>
      ) : (
        <div className="h-16 bg-gradient-to-r from-slate-100 to-slate-200 flex items-center justify-center">
          <ImageIcon size={20} className="text-slate-300" />
        </div>
      )}

      {/* Info */}
      <div className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-black text-slate-900 tracking-tight truncate">{banner.title_ko || banner.title_en || '(제목 없음)'}</p>
            {banner.desc_ko && <p className="text-[11px] text-slate-400 mt-0.5 truncate">{banner.desc_ko}</p>}
          </div>
          <div className="flex gap-2 shrink-0">
            <button onClick={onEdit} className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 text-slate-500 hover:bg-slate-200 active:scale-90 transition-all">
              <Edit2 size={14} />
            </button>
            <button onClick={onDelete} className="w-8 h-8 flex items-center justify-center rounded-lg bg-rose-50 text-rose-400 hover:bg-rose-100 active:scale-90 transition-all">
              <Trash2 size={14} />
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <span className="px-2.5 py-1 rounded-full bg-slate-100 text-[9px] font-black text-slate-500 uppercase tracking-widest">
            {POSITION_LABELS[banner.position] ?? banner.position}
          </span>
          {isWC && (
            <span className="px-2.5 py-1 rounded-full bg-amber-100 text-[9px] font-black text-amber-600 uppercase tracking-widest">월드컵</span>
          )}
          <span className={`px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${
            banner.is_published ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'
          }`}>
            {banner.is_published ? '노출' : '숨김'}
          </span>
          <span className="px-2.5 py-1 rounded-full bg-slate-100 text-[9px] font-black text-slate-400 uppercase tracking-widest">
            순서 {banner.sort_order}
          </span>
        </div>

        {(banner.start_at || banner.end_at) && (
          <p className="text-[10px] text-slate-400">
            {banner.start_at && new Date(banner.start_at).toLocaleString('ko-KR', { dateStyle: 'short', timeStyle: 'short' })}
            {banner.start_at && banner.end_at && ' ~ '}
            {banner.end_at && new Date(banner.end_at).toLocaleString('ko-KR', { dateStyle: 'short', timeStyle: 'short' })}
          </p>
        )}
      </div>
    </div>
  );
};
